import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { retention } from '../../../../../config/portal.json';
import { Dialog } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';

/**
 * The scanned card or badge of a contact (decision of Nov 3, 2026): to
 * correct what the scan read while looking at it. Served by the portal to
 * whoever may see the contact, each opening in the audit log; the device
 * that scanned it keeps its own copy, encrypted, to show it offline. Kept
 * `retention.cardImageDays` days after the scan, then removed everywhere.
 */

const COPIES = 'cardCopies';
const copyKey = (id: string) => `card:${id}`;
const DAY = 86_400_000;

async function toBase64(blob: Blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function fromBase64(data: string, type: string) {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

/** The scanning device keeps the card it sent, for the days it is kept. */
export async function keepCardCopy(store: LocalStore, contactId: string, photo: Blob) {
  await store.setMeta(copyKey(contactId), {
    type: photo.type || 'image/jpeg',
    data: await toBase64(photo),
  });
  const list = (await store.getMeta<{ id: string; at: number }[]>(COPIES).catch(() => null)) ?? [];
  await store.setMeta(COPIES, [
    ...list.filter((c) => c.id !== contactId),
    { id: contactId, at: Date.now() },
  ]);
}

export async function dropCardCopy(store: LocalStore, contactId: string) {
  await store.setMeta(copyKey(contactId), null);
  const list = (await store.getMeta<{ id: string; at: number }[]>(COPIES).catch(() => null)) ?? [];
  await store.setMeta(
    COPIES,
    list.filter((c) => c.id !== contactId),
  );
}

/** The copies older than the days a card is kept leave the device. */
export async function purgeCardCopies(store: LocalStore, now = Date.now()) {
  const list = (await store.getMeta<{ id: string; at: number }[]>(COPIES).catch(() => null)) ?? [];
  const old = list.filter((c) => now - c.at > retention.cardImageDays * DAY);
  for (const c of old) await store.setMeta(copyKey(c.id), null);
  if (old.length > 0)
    await store.setMeta(
      COPIES,
      list.filter((c) => !old.includes(c)),
    );
}

async function cardCopy(store: LocalStore, contactId: string): Promise<Blob | null> {
  const kept = await store
    .getMeta<{ type: string; data: string }>(copyKey(contactId))
    .catch(() => null);
  return kept ? fromBase64(kept.data, kept.type) : null;
}

type CardState = { status: 'loading' | 'none' | 'removed' } | { status: 'shown'; src: string };

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** The card of a contact: the portal's (one request, audited), else this device's copy. */
export function useCardImage(contact: LocalRecord): CardState {
  const { store } = useOffline();
  const id = contact.id;
  const key = str(contact.card_image_key);
  const removed = Boolean(contact.card_removed_at);
  const reading = contact.reading_status === 'waiting' || contact.reading_status === 'reading';
  const [state, setState] = useState<CardState>({ status: 'loading' });

  useEffect(() => {
    let current = true;
    let url: string | null = null;
    const show = (blob: Blob) => {
      url = URL.createObjectURL(blob);
      if (current) setState({ status: 'shown', src: url });
    };
    void (async () => {
      if (removed) {
        if (store) await dropCardCopy(store, id).catch(() => undefined);
        if (current) setState({ status: 'removed' });
        return;
      }
      if (store) await purgeCardCopies(store).catch(() => undefined);
      // Closed meanwhile: no request for a sheet no one sees.
      if (!current) return;
      if (key) {
        try {
          const res = await fetch(`/api/capture/scans/${encodeURIComponent(id)}/card`, {
            credentials: 'same-origin',
          });
          if (res.status === 410) {
            if (store) await dropCardCopy(store, id).catch(() => undefined);
            if (current) setState({ status: 'removed' });
            return;
          }
          if (res.ok) return show(await res.blob());
        } catch {
          // No network: this device's copy, if it scanned the card.
        }
      }
      const copy = store ? await cardCopy(store, id).catch(() => null) : null;
      if (copy) return show(copy);
      if (current) setState({ status: 'none' });
    })();
    return () => {
      current = false;
      if (url) URL.revokeObjectURL(url);
    };
    // reading: once read, the portal's straightened card replaces the copy.
  }, [id, key, removed, reading, store]);
  return state;
}

/** The card in a small frame; a touch opens it full screen, to zoom. */
export function CardThumb({ contact }: { contact: LocalRecord }) {
  const card = useCardImage(contact);
  const full = useFullCard();
  if (card.status === 'removed') {
    return (
      <p className="note" style={{ margin: 0 }}>
        Card image removed after {retention.cardImageDays} days
      </p>
    );
  }
  if (card.status !== 'shown') return null;
  return (
    <>
      <button
        type="button"
        className="zoomimg card-thumb"
        aria-label="Open the scanned card full screen"
        onClick={full.show}
      >
        <img src={card.src} alt="Scanned card" />
      </button>
      {full.open && <CardFull src={card.src} onClose={full.close} />}
    </>
  );
}

/**
 * The card full screen as a step of the history (Nov 4, 2026): Back (the
 * phone's button or swipe, the browser's) closes it and returns to the
 * contact's sheet, never the sheet itself.
 */
function useFullCard() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state as Record<string, unknown> | null) ?? {};
  return {
    open: state.cardFull === true,
    show: () =>
      navigate(
        { pathname: location.pathname, search: location.search },
        { state: { ...state, cardFull: true } },
      ),
    close: () => navigate(-1),
  };
}

/**
 * Full screen: the card as large as the screen allows; a touch zooms in and
 * out, a pinch zooms on a phone. Back to contact, always in view, returns to
 * the sheet, as the cross, Escape and the Back gesture do.
 */
function CardFull({ src, onClose }: { src: string; onClose(): void }) {
  const [zoom, setZoom] = useState(false);
  return (
    <Dialog title="Scanned card" onClose={onClose} className="card-full">
      <button type="button" className="btn primary card-back" onClick={onClose}>
        Back to contact
      </button>
      <button
        type="button"
        className={`card-full-img${zoom ? ' zoom' : ''}`}
        aria-label={zoom ? 'Zoom out' : 'Zoom in'}
        onClick={() => setZoom((z) => !z)}
      >
        <img src={src} alt="Scanned card, full screen" />
      </button>
    </Dialog>
  );
}

/** Edit and Verify: the card beside the fields (above them on a phone). */
export function WithCard({
  contact,
  children,
}: {
  contact: LocalRecord;
  children: React.ReactNode;
}) {
  const card = useCardImage(contact);
  const full = useFullCard();
  if (card.status !== 'shown') {
    return (
      <>
        {card.status === 'removed' && (
          <p className="note" style={{ margin: 0 }}>
            Card image removed after {retention.cardImageDays} days
          </p>
        )}
        {children}
      </>
    );
  }
  return (
    <div className="with-card">
      <div className="with-card-img">
        <button
          type="button"
          className="zoomimg card-side"
          aria-label="Open the scanned card full screen"
          onClick={full.show}
        >
          <img src={card.src} alt="Scanned card" />
        </button>
      </div>
      <div className="with-card-fields">{children}</div>
      {full.open && <CardFull src={card.src} onClose={full.close} />}
    </div>
  );
}
